import React from "react";
import { pxToRem } from "src/pages/Insights/transformer/dashboardUtils";

interface INSizeLabelProps {
	nSize: number | { min: number | "no data"; max: number | "no data" } | "no data" | null;
	threshold?: number;
	className?: string;
	isCompact?: boolean;
	/**
	 * Font size in px applied as inline style.
	 * Defaults to 11 — identical to the previous hardcoded text-[11px].
	 * Pass scaled.typography.nSize from useScaledConfig() in multi-tile mode.
	 */
	fontSize?: number;
}

export const NSizeLabel: React.FC<INSizeLabelProps> = ({
	nSize,
	threshold = 30,
	className = "text-[#2F4B7F]",
	isCompact = false,
	fontSize = 11, // ← default preserves existing behaviour exactly
}) => {
	const effectiveFontSize = isCompact ? Math.min(fontSize, 8) : fontSize;

	const fontStyle: React.CSSProperties = {
		fontSize: pxToRem(effectiveFontSize),
		lineHeight: isCompact
			? pxToRem(Math.max(effectiveFontSize * 1.25, effectiveFontSize + 2))
			: undefined,
		// whiteSpace: "nowrap",
	};

	// ── Null / "no data" path ───────────────────────────────────────────────
	if (nSize == null || (typeof nSize === "string" && nSize.toLowerCase() === "no data")) {
		return (
			<span className={`text-center ${className}`} style={fontStyle}>
				n = <span className="text-[--nSizeLabel-color]">0</span>
			</span>
		);
	}

	// ── Extract raw values ──────────────────────────────────────────────────
	const rawMin = typeof nSize === "object" ? nSize.min : nSize;
	const rawMax = typeof nSize === "object" ? nSize.max : nSize;

	// Normalise "no data" / null / NaN sentinels to 0
	const min = rawMin == null || Number.isNaN(rawMin) || (typeof rawMin === "string" && rawMin.toLowerCase() === "no data") ? 0 : rawMin;

	const max = rawMax == null || Number.isNaN(rawMax) || (typeof rawMax === "string" && rawMax.toLowerCase() === "no data") ? 0 : rawMax;

	// Guard against sentinel values left by buildNSizeMap when no data was found
	if (min === Number.MAX_SAFE_INTEGER || max === Number.MIN_SAFE_INTEGER) {
		return null;
	}

	const isRange = min !== max;
	const minBelowThreshold = min <= threshold;
	const maxBelowThreshold = max <= threshold;

	// ── Normal render ───────────────────────────────────────────────────────
	return (
		<span className={`text-center ${className}`} style={fontStyle}>
			n = <span className={minBelowThreshold ? "text-[--nSizeLabel-color]" : "text-[#2F4B7F]"}>{min}</span>
			{isRange && (
				<>
					<span className={`text-gray-400 mx-0.5`}>-</span>
					<span className={maxBelowThreshold ? "text-[--nSizeLabel-color]" : "text-[#2F4B7F]"}>{max}</span>
				</>
			)}
		</span>
	);
};
